"use client";

import { useEffect } from "react";
import { useAtom } from "jotai";
import Hotjar from "@hotjar/browser";
import { acceptedCookiePolicyAtom } from "~/atoms";
import { CookieTypeEnum } from "~/types-and-enums/cookieTypes";

/**
 * Hotjar tracking component
 * Only loads if Behavioral cookies are accepted
 *
 * Uses the official @hotjar/browser package
 *
 * @returns null (no UI rendered)
 */
const HotjarWrapper = () => {
  const [acceptedCookiePolicy] = useAtom(acceptedCookiePolicyAtom);
  const hotjarSiteId = process.env.NEXT_PUBLIC_HOTJAR_SITE_ID;
  const hotjarVersion = 6; // Default Hotjar version

  useEffect(() => {
    // Only load Hotjar if:
    // 1. Hotjar Site ID is configured
    // 2. User has accepted Behavioral cookies
    const hasBehavioralConsent = acceptedCookiePolicy.includes(CookieTypeEnum.Behavioral);

    if (!hotjarSiteId || !hasBehavioralConsent) {
      return;
    }

    // Check if already initialized to prevent duplicate initialization
    if (typeof window !== "undefined") {
      const windowWithHotjar = window as typeof window & { hj?: unknown };
      if (windowWithHotjar.hj) {
        return;
      }
    }

    try {
      const siteId = parseInt(hotjarSiteId, 10);

      // Initialize Hotjar
      Hotjar.init(siteId, hotjarVersion);
    } catch (error) {
      // Silently handle CSP errors - they're expected in some environments
      if (error instanceof Error && error.message.includes("Content Security Policy")) {
        console.warn(
          "Hotjar blocked by Content Security Policy. Please add 'https://static.hotjar.com' to script-src."
        );
      } else {
        console.error("Failed to initialize Hotjar:", error);
      }
    }
  }, [hotjarSiteId, acceptedCookiePolicy, hotjarVersion]);

  return null;
};

export default HotjarWrapper;
